import {
  Container,
  Dialog,
  GhostButton,
  Layout,
  Modal,
  PrimaryButton,
  ProgressLoader,
  SecondaryTopNav,
  Text,
} from "@certn/cascadia-react";
import { css } from "@emotion/css";
import { t } from "i18next";
import { PropsWithChildren, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { ExitClientProfileFormModal } from "./ExitClientProfileFormModal";
import {
  CreateBillingProfileContext,
  initialBillingProfileValues,
} from "../CreateBillingProfileContext";
import { PageTitle } from "../components/PageTitle";

interface Props extends PropsWithChildren {
  disableCompletion?: boolean;
  submitting?: boolean;
  onComplete?: () => void;
  formIsDirty?: boolean;
}

export function CreateBillingProfilePageLayout(props: Props) {
  const { disableCompletion, submitting, onComplete, children } = props;

  const navigate = useNavigate();
  const {
    billingProfile,
    currentStep,
    goToPrevStep,
    formHasData,
    setFormHasData,
  } = useContext(CreateBillingProfileContext);
  const [displayModal, setDisplayModal] = useState(false);

  useEffect(() => {
    setFormHasData(
      props.formIsDirty || billingProfile !== initialBillingProfileValues
    );
  }, [billingProfile, formHasData, props.formIsDirty, setFormHasData]);

  const handleExitForm = () => {
    if (formHasData || disableCompletion) {
      setDisplayModal(true);
    } else {
      navigate(`/billing/billing-profiles`);
    }
  };

  const steps = [t("Create Billing Profile"), t("Billing Contact Information")];
  const atLastStep = currentStep >= steps.length - 1;
  const readableCurrentStep = currentStep + 1;
  const onFirstStep = currentStep === 0;

  return (
    <Layout
      layout="narrow"
      header={
        <SecondaryTopNav
          testID="close-button"
          leftIcon={onFirstStep ? "close" : "arrow_back"}
          onleftIconOnPress={onFirstStep ? handleExitForm : goToPrevStep}
        >
          {currentStep > 0 && (
            <GhostButton
              onPress={handleExitForm}
              size="medium"
              testID="previous-step-button"
            >
              {t("Cancel")}
            </GhostButton>
          )}

          <PrimaryButton
            onPress={onComplete}
            isDisabled={disableCompletion}
            isLoading={submitting}
          >
            {!atLastStep ? t("Next step") : t("Complete billing profile")}
          </PrimaryButton>
        </SecondaryTopNav>
      }
    >
      <Container
        testID={
          steps[currentStep].replaceAll(" ", "-").toLowerCase() + "-container"
        }
      >
        <Modal isOpen={displayModal}>
          <Dialog>
            {() => (
              <ExitClientProfileFormModal
                onBackToHub={() => {
                  navigate(`/billing/billing-profiles`);
                }}
                onClose={() => {
                  setDisplayModal(false);
                }}
              />
            )}
          </Dialog>
        </Modal>
        <div className={styles.container}>
          <div>
            <Text style="input/section-title">
              {t(`step {{readableCurrentStep}} of {{steps}}`, {
                readableCurrentStep,
                steps: steps.length,
              })}
            </Text>
            <PageTitle>{steps[currentStep]}</PageTitle>
          </div>
          <div style={{ paddingBottom: "var(--space-3)" }}>
            <ProgressLoader floating value={currentStep === 0 ? 0 : 50} />
          </div>
          {children}
        </div>
      </Container>
    </Layout>
  );
}

const styles = {
  container: css({
    paddingLeft: "var(--space-8)",
    paddingRight: "var(--space-8)",
    paddingTop: "var(--space-7)",
    paddingBottom: "var(--space-7)",
    gap: "var(--space-3)",
    display: "flex",
    flexDirection: "column",
  }),
};
